import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { Table, Select } from "../../../shared/ui";
import { listAdminUsers, type AdminUser, type Page } from "../../../features/admin/api/adminApi";
import { PageHeader, StatusPill, shortDate } from "./_shared";

export default function AdminUsers() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState<Page<AdminUser> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") ?? "");

  const status = params.get("accountStatus") ?? "";
  const role = params.get("role") ?? "";
  const page = Number(params.get("page")) || 1;

  useEffect(() => {
    setLoading(true);
    listAdminUsers({ page, search, accountStatus: status, role })
      .then(setData)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [page, status, role, search]);

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    next.delete("page");
    setParams(next);
  };

  return (
    <>
      <PageHeader title="Users" subtitle={data ? `${data.total} total` : undefined} />
      <div className="p-4 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, company…"
              className="w-full rounded-lg border border-ink-200 py-2 pl-9 pr-3 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
            />
          </div>
          <div className="w-[160px]">
            <Select
              label="Status"
              value={status}
              onChange={(e) => setParam("accountStatus", e.target.value)}
              options={[
                { value: "", label: "All statuses" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "banned", label: "Banned" },
              ]}
            />
          </div>
          <div className="w-[160px]">
            <Select
              label="Role"
              value={role}
              onChange={(e) => setParam("role", e.target.value)}
              options={[
                { value: "", label: "All roles" },
                { value: "saas_user", label: "User" },
                { value: "saas_owner", label: "Super admin" },
              ]}
            />
          </div>
        </div>

        <div className="surface-card overflow-hidden p-0">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-ink-400">
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <Table
              rows={data?.rows ?? []}
              rowKey={(u) => u._id}
              onRowClick={(u) => navigate(`/admin/users/${u._id}`)}
              emptyState="No users match."
              columns={[
                { id: "name", header: "Name", cell: (u) => (
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink-900">{u.name}</p>
                    <p className="truncate text-[11.5px] text-ink-400">{u.email}</p>
                  </div>
                ) },
                { id: "company", header: "Company", hideOnMobile: true, cell: (u) => u.companyName || "—" },
                { id: "role", header: "Role", cell: (u) => (u.role === "saas_owner" ? "Super admin" : "User") },
                { id: "status", header: "Status", cell: (u) => <StatusPill status={u.accountStatus} /> },
                { id: "last", header: "Last login", hideOnMobile: true, cell: (u) => shortDate(u.lastLoginAt) },
                { id: "joined", header: "Joined", align: "right", cell: (u) => shortDate(u.createdAt) },
              ]}
            />
          )}
        </div>

        {data && data.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2 text-[12.5px]">
            <button disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} className="rounded-lg border border-ink-200 px-3 py-1.5 disabled:opacity-40">Prev</button>
            <span className="text-ink-500">Page {page} of {data.totalPages}</span>
            <button disabled={page >= data.totalPages} onClick={() => setParam("page", String(page + 1))} className="rounded-lg border border-ink-200 px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </>
  );
}
